import './SwitchColorButton.scss';

import type { RootState } from 'store';
import * as config from '@/constants/Config';

import Icon from '@/components/Icon/Icon';
import { ACCOUNT_TYPES } from '@/constants/Home';
import { useAppSelector } from '@/store/hooks';
import { applyCorporateDesignToolParamIfNeeded } from '@/utilities/DesignToolUrlUtils';
import { captureStockAlertOptionClicked } from '@/mixpanel/MixpanelEvents';
import { useGetSimilarProductsQuery } from '@/api/SimilarProductQuery';
import useRedirectWithUTM from '@/hooks/useRedirectWithUTM';

interface SwitchColorButtonProps {
  label: string;
  productStyleCode: string;
  color: string;
  source: string;
  message: string;
  productBrand: string;
  section: string;
  seeMoreUrl?: string;
  hexCode: string;
  proofItemId?: string | number;
}

const SwitchColorButton = ({
  label,
  productStyleCode,
  color,
  source,
  message,
  productBrand,
  section,
  hexCode,
  seeMoreUrl,
  proofItemId,
}: SwitchColorButtonProps) => {
  const redirectToWithUTM = useRedirectWithUTM();
  const { user } = useAppSelector((state: RootState) => state.user);
  const isCorporateAccount = user?.accountType === ACCOUNT_TYPES.CORPORATE;

  const { data: similarProductsData } = useGetSimilarProductsQuery(
    {
      styleCode: productStyleCode,
      hexCode,
      forceMode: true,
    },
    {
      skip: false,
      refetchOnMountOrArgChange: true,
    }
  );
  const finalSeeMoreUrl = similarProductsData?.seeMoreURL ?? seeMoreUrl ?? '';

  const switchColorTargetUrl = applyCorporateDesignToolParamIfNeeded(
    proofItemId
      ? `${config.DESIGN_TOOL}?proofItemId=${proofItemId}&altColor=true`
      : `${config.DESIGN_TOOL}?altColor=true`,
    isCorporateAccount
  );

  const switchProductTargetUrl = applyCorporateDesignToolParamIfNeeded(
    config.DESIGN_TOOL + (finalSeeMoreUrl ? finalSeeMoreUrl.replace(/^\//, '') : ''),
    isCorporateAccount
  );

  const handleSwitchColorClick: React.MouseEventHandler<HTMLAnchorElement> = (event) => {
    event.preventDefault();
    event.stopPropagation();
    captureStockAlertOptionClicked(
      productStyleCode,
      color,
      source,
      message,
      'Switch to a color with stock',
      productBrand,
      section
    );
    redirectToWithUTM(switchColorTargetUrl);
  };

  const handleSwitchProductClick: React.MouseEventHandler<HTMLAnchorElement> = (event) => {
    event.preventDefault();
    event.stopPropagation();
    captureStockAlertOptionClicked(
      productStyleCode,
      color,
      source,
      message,
      'Switch to a similar product with stock',
      productBrand,
      section
    );
    redirectToWithUTM(switchProductTargetUrl);
  };

  return (
    <div className="stock-btn">
      <span>{label}</span>
      <a
        href={switchColorTargetUrl}
        className="stock-btn__switch-color"
        onClick={handleSwitchColorClick}
      >
        <div className="btn-content">
          <div>Switch to a Color with Stock</div>
          <Icon
            source="/src/assets/svg/chevron-right-icon.svg"
            height="8"
            width="8"
            className="right-chevron-color"
          />
        </div>
      </a>

      <a
        href={switchProductTargetUrl}
        className="stock-btn__switch-product"
        onClick={handleSwitchProductClick}
      >
        <div className="btn-content">
          <div>Switch to a Similar Product with Stock</div>
          <Icon
            source="/src/assets/svg/chevron-right-icon.svg"
            height="8"
            width="8"
            className="right-chevron-product"
          />
        </div>
      </a>
    </div>
  );
};

export default SwitchColorButton;
